import { 
  Invoice, 
  CreateInvoiceRequest, 
  UpdateInvoiceRequest, 
  InvoiceFilters,
  PaginatedResponse,
  ApiResponse,
  ZatcaStatus,
  PaginationParams,
  FileAttachment
} from '../../types/api'
import { apiClient } from '../../utils/api'

export class InvoiceService {
  private readonly basePath = '/invoices'

  // CRUD Operations
  async getInvoices(
    filters?: InvoiceFilters, 
    pagination?: PaginationParams
  ): Promise<ApiResponse<PaginatedResponse<Invoice>>> {
    const params = {
      ...filters,
      ...pagination
    }
    return apiClient.get(`${this.basePath}`, params)
  }

  async getInvoice(id: string): Promise<ApiResponse<Invoice>> {
    return apiClient.get(`${this.basePath}/${id}`)
  }

  async createInvoice(data: CreateInvoiceRequest): Promise<ApiResponse<Invoice>> {
    return apiClient.post(`${this.basePath}`, data)
  }

  async updateInvoice(id: string, data: UpdateInvoiceRequest): Promise<ApiResponse<Invoice>> {
    return apiClient.put(`${this.basePath}/${id}`, data)
  }

  async deleteInvoice(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`${this.basePath}/${id}`)
  }

  // Status Management
  async updateStatus(id: string, status: string): Promise<ApiResponse<Invoice>> {
    return apiClient.patch(`${this.basePath}/${id}/status`, { status })
  }

  async markAsPaid(id: string, paymentData?: any): Promise<ApiResponse<Invoice>> {
    return apiClient.patch(`${this.basePath}/${id}/mark-paid`, paymentData)
  }

  async markAsSent(id: string): Promise<ApiResponse<Invoice>> {
    return apiClient.patch(`${this.basePath}/${id}/mark-sent`)
  }

  // Search and Filtering
  async searchInvoices(query: string): Promise<ApiResponse<Invoice[]>> {
    return apiClient.get(`${this.basePath}/search`, { q: query })
  }

  async getInvoicesByCustomer(customerId: string): Promise<ApiResponse<Invoice[]>> {
    return apiClient.get(`${this.basePath}/customer/${customerId}`)
  }

  async getInvoicesByDateRange(from: string, to: string): Promise<ApiResponse<Invoice[]>> {
    return apiClient.get(`${this.basePath}/date-range`, { from, to })
  }

  // Calculations
  async calculateInvoice(data: CreateInvoiceRequest): Promise<ApiResponse<{
    subtotal: number
    tax_amount: number
    discount_amount: number
    total: number
  }>> {
    return apiClient.post(`${this.basePath}/calculate`, data)
  }

  async validateInvoice(data: CreateInvoiceRequest): Promise<ApiResponse<{
    valid: boolean
    errors: string[]
    warnings: string[]
  }>> {
    return apiClient.post(`${this.basePath}/validate`, data)
  }

  // Duplication and Templates
  async duplicateInvoice(id: string): Promise<ApiResponse<Invoice>> {
    return apiClient.post(`${this.basePath}/${id}/duplicate`)
  }

  async createFromTemplate(templateId: string, data: Partial<CreateInvoiceRequest>): Promise<ApiResponse<Invoice>> {
    return apiClient.post(`${this.basePath}/templates/${templateId}/create`, data)
  }

  // Export and Printing
  async exportInvoice(id: string, format: 'pdf' | 'excel' | 'csv'): Promise<ApiResponse<{ url: string }>> {
    return apiClient.get(`${this.basePath}/${id}/export/${format}`)
  }

  async printInvoice(id: string, options?: {
    copies?: number
    printer?: string
    paper_size?: string
  }): Promise<ApiResponse<{ success: boolean, job_id?: string }>> {
    return apiClient.post(`${this.basePath}/${id}/print`, options)
  }

  async getPrintPreview(id: string): Promise<ApiResponse<{ html: string, pdf_url?: string }>> {
    return apiClient.get(`${this.basePath}/${id}/preview`)
  }

  // ZATCA Compliance
  async getZatcaStatus(id: string): Promise<ApiResponse<ZatcaStatus>> {
    return apiClient.get(`${this.basePath}/${id}/zatca/status`)
  }

  async generateZatcaQR(id: string): Promise<ApiResponse<{ qr_code: string }>> {
    return apiClient.post(`${this.basePath}/${id}/zatca/qr`)
  }

  async validateZatcaCompliance(id: string): Promise<ApiResponse<{
    compliant: boolean
    errors: Array<{ code: string, message: string }>
    warnings: Array<{ code: string, message: string }>
  }>> {
    return apiClient.post(`${this.basePath}/${id}/zatca/validate`)
  }

  async reportToZatca(id: string): Promise<ApiResponse<{
    success: boolean
    uuid?: string
    hash?: string
    reported_at?: string
  }>> {
    return apiClient.post(`${this.basePath}/${id}/zatca/report`)
  }

  async clearanceFromZatca(id: string): Promise<ApiResponse<{
    success: boolean
    cleared_at?: string
    clearance_status?: string
  }>> {
    return apiClient.post(`${this.basePath}/${id}/zatca/clearance`)
  }

  // File Attachments
  async getAttachments(id: string): Promise<ApiResponse<FileAttachment[]>> {
    return apiClient.get(`${this.basePath}/${id}/attachments`)
  }

  async uploadAttachment(
    id: string, 
    file: File, 
    category: string
  ): Promise<ApiResponse<FileAttachment>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('category', category)
    
    return apiClient.post(`${this.basePath}/${id}/attachments`, formData)
  }

  async deleteAttachment(id: string, attachmentId: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`${this.basePath}/${id}/attachments/${attachmentId}`)
  }

  // Batch Operations
  async bulkUpdate(ids: string[], data: Partial<UpdateInvoiceRequest>): Promise<ApiResponse<{
    updated: number
    failed: Array<{ id: string, error: string }>
  }>> {
    return apiClient.put(`${this.basePath}/bulk`, { ids, data })
  }

  async bulkDelete(ids: string[]): Promise<ApiResponse<{
    deleted: number
    failed: Array<{ id: string, error: string }>
  }>> {
    return apiClient.delete(`${this.basePath}/bulk`, { ids })
  }

  async bulkExport(ids: string[], format: 'pdf' | 'excel'): Promise<ApiResponse<{ url: string }>> {
    return apiClient.post(`${this.basePath}/bulk/export`, { ids, format })
  }

  // Analytics and Statistics
  async getInvoiceStatistics(filters?: {
    period?: 'today' | 'week' | 'month' | 'quarter' | 'year'
    date_from?: string
    date_to?: string
    customer_id?: string
    branch_id?: string
  }): Promise<ApiResponse<{
    total_count: number
    total_amount: number
    paid_amount: number
    pending_amount: number
    overdue_amount: number
    average_amount: number
    status_breakdown: Record<string, number>
    monthly_trend: Array<{ month: string, count: number, amount: number }>
  }>> {
    return apiClient.get(`${this.basePath}/statistics`, filters)
  }

  // Payment Tracking
  async recordPayment(id: string, paymentData: {
    amount: number
    method: string
    reference?: string
    notes?: string
    payment_date?: string
  }): Promise<ApiResponse<Invoice>> {
    return apiClient.post(`${this.basePath}/${id}/payments`, paymentData)
  }

  async getPaymentHistory(id: string): Promise<ApiResponse<Array<{
    id: string
    amount: number
    method: string
    reference?: string
    notes?: string
    payment_date: string
    created_at: string
  }>>> {
    return apiClient.get(`${this.basePath}/${id}/payments`)
  }

  // Email and Communication
  async sendByEmail(id: string, emailData: {
    to: string[]
    cc?: string[]
    subject?: string
    message?: string
    include_pdf?: boolean
  }): Promise<ApiResponse<{ sent: boolean, message_id?: string }>> {
    return apiClient.post(`${this.basePath}/${id}/email`, emailData)
  }

  async sendReminder(id: string, reminderType: 'payment' | 'overdue'): Promise<ApiResponse<{ sent: boolean }>> {
    return apiClient.post(`${this.basePath}/${id}/reminder`, { type: reminderType })
  }

  // Recent and Quick Access
  async getRecentInvoices(limit: number = 10): Promise<ApiResponse<Invoice[]>> {
    return apiClient.get(`${this.basePath}/recent`, { limit })
  }

  async getDraftInvoices(): Promise<ApiResponse<Invoice[]>> {
    return apiClient.get(`${this.basePath}/drafts`)
  }

  async getOverdueInvoices(): Promise<ApiResponse<Invoice[]>> {
    return apiClient.get(`${this.basePath}/overdue`)
  }
}

// Create and export service instance
export const invoiceService = new InvoiceService() 